import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ListChecks, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackPageView } from '../utils/analytics';

const WelcomePage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Redirect to homepage if not logged in, or to dashboard if already started
  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    // Check if user has started any activities
    const checkOnboardingStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activities/responses/user/${user.id}`);
        const activities = await response.json();
        
        if (activities.length > 0) {
          // User has started - redirect to dashboard
          navigate('/dashboard');
        } else {
          // User hasn't started - show welcome page
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setLoading(false);
      }
    };
    
    checkOnboardingStatus();
  }, [user, navigate]);
  
  // Calculate activities per week based on commitment
  const activitiesPerWeek = user?.weekly_commitment === 60 ? 1 : (user?.weekly_commitment === 120 ? 2 : 3);
  
  // Track page view
  useEffect(() => {
    if (user && !loading) {
      trackPageView('welcome');
    }
  }, [user, loading]);
  
  // Show loading or nothing while checking
  if (!user || loading) {
    return null;
  }
  
  const handleGetStarted = () => {
    // Mark onboarding as seen
    localStorage.setItem('kodo_onboarding_seen', 'true');
    navigate('/week/1?activity=1');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="max-w-xl w-full">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Welcome to Kodo, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 text-lg">
              You've taken an important step. Let's get you moving.
            </p>
          </div>

          {/* Week 1 info */}
          <div className="space-y-5 mb-8">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              Week 1: Self-Discovery
            </h2>
            
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <ListChecks className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{activitiesPerWeek === 1 ? '1 activity' : `${activitiesPerWeek} activities`} to complete</h3>
                <p className="text-slate-600">
                  Reflect on your career journey and what matters most to you.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Personalised feedback</h3>
                <p className="text-slate-600">
                  Complete an activity to receive tailored insights and weekly tasks.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Go at your own pace</h3>
                <p className="text-slate-600">
                  Aim to complete within 7 days, but take the time you need.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <Button
            onClick={handleGetStarted}
            className="w-full rounded-full py-6 text-lg font-semibold bg-primary hover:bg-primary/90"
            data-testid="start-week-one-btn"
          >
            Start Week 1
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
